/**
 * Métricas de entrega a prod por equipo (ECO), con los datos que ya tiene Releases: despliegues del CD (30 días),
 * rollbacks hechos desde Backstage y build-once de la versión vigente. Mismo criterio que DORA: deploy = CD ok.
 */

type Row = { slug: string; env: string; date: string; state: string };
type Rollback = { at: string; slug: string; environment: string };
type Matrix = { slug: string; prodImage?: { reused: boolean } };

export type TeamMetrics = {
  team: string;
  services: number;
  deploys: number;
  perWeek: number;
  failed: number;
  rollbacks: number;
  failureRate?: number;
  recoveryHours?: number;
  reusedImage: number;
  withImage: number;
};

const HOUR = 3_600_000;
const median = (xs: number[]) => {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** Una fila por equipo más "Todos", ordenadas por despliegues. */
export function teamMetrics(
  rows: Row[],
  rollbacks: Rollback[],
  matrix: Matrix[],
  owners: Record<string, string>,
  days = 30,
  now = Date.now(),
): TeamMetrics[] {
  const since = now - days * 24 * HOUR;
  const prod = rows.filter(r => r.env === 'prod' && Date.parse(r.date) >= since);
  const prodRollbacks = rollbacks.filter(r => r.environment === 'prod' && Date.parse(r.at) >= since);
  const teamOf = (slug: string) => owners[slug] || 'sin equipo';

  const build = (team: string, inTeam: (slug: string) => boolean): TeamMetrics => {
    const mine = prod.filter(r => inTeam(r.slug));
    const ok = mine.filter(r => r.state === 'Desplegado');
    const failed = mine.filter(r => r.state === 'Falló');
    const rb = prodRollbacks.filter(r => inTeam(r.slug)).length;
    // Recuperación: de un despliegue fallido al siguiente despliegue bueno del mismo servicio.
    const recovery = failed
      .map(f => {
        const next = ok.filter(o => o.slug === f.slug && o.date > f.date).sort((a, b) => a.date.localeCompare(b.date))[0];
        return next ? (Date.parse(next.date) - Date.parse(f.date)) / HOUR : undefined;
      })
      .filter((h): h is number => h !== undefined);
    const images = matrix.filter(m => inTeam(m.slug) && m.prodImage);
    const attempts = ok.length + failed.length;
    return {
      team,
      services: Object.keys(owners).filter(inTeam).length,
      deploys: ok.length,
      perWeek: Math.round((ok.length / days) * 7 * 10) / 10,
      failed: failed.length,
      rollbacks: rb,
      // Tasa de fallo de cambio: despliegues que fallaron o hubo que revertir, sobre los intentos.
      failureRate: attempts ? (failed.length + rb) / attempts : undefined,
      recoveryHours: median(recovery),
      reusedImage: images.filter(m => m.prodImage!.reused).length,
      withImage: images.length,
    };
  };

  const teams = [...new Set(Object.values(owners).map(o => o || 'sin equipo'))];
  return [
    build('Todos', () => true),
    ...teams.map(t => build(t, slug => teamOf(slug) === t)).sort((a, b) => b.deploys - a.deploys || a.team.localeCompare(b.team)),
  ];
}
